import { arrayUpdateInPlace, moveToFulfilled, moveToPending, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { AddGroupLeaderFulfilledAction } from '../../domains/groupAddRemoveLeader/action.types';
import type {
  GroupAddUserFulfilledAction,
  GroupRemoveUserFulfilledAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import type { GroupAddUserByEmailFulfilledAction } from '../../domains/groupAddRemoveUser/addUserByEmailActions.types';
import type {
  GroupMakeAdminFulfilledAction,
  GroupRevokeAdminFulfilledAction,
} from '../../domains/groupMakeRevokeAdmin/actions.types';
import type {
  GroupMemberPanelsLoadFulfilledAction,
  GroupMemberPanelsLoadMoreFulfilledAction,
  GroupMemberPanelsLoadPendingAction,
} from '../../domains/groupMemberPanels/actions.types';
import type { GroupMemberSearchRemoveAction } from '../../domains/groupMemberSearch/actions.types';
import type { ResolveGroupMembershipFromMembershipPanelFulfilledAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import type { Group, GroupMembersEdge, User } from '../types';

import type { GroupMemberPanel, GroupMemberPanels } from './types';

export type GroupMemberPanelsAction =
  | GroupMemberPanelsLoadFulfilledAction
  | GroupMemberPanelsLoadPendingAction
  | ResolveGroupMembershipFromMembershipPanelFulfilledAction
  | GroupAddUserFulfilledAction
  | GroupRemoveUserFulfilledAction
  | GroupMakeAdminFulfilledAction
  | GroupRevokeAdminFulfilledAction
  | GroupMemberPanelsLoadMoreFulfilledAction
  | GroupMemberSearchRemoveAction
  | GroupAddUserByEmailFulfilledAction
  | AddGroupLeaderFulfilledAction;

const initialState: GroupMemberPanels = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithUpdatedFulfilledGroupMembers = (
  state: GroupMemberPanels,
  id: Group['id'],
  updatedFields: Partial<GroupMemberPanel>
) => update(state, id, updatedFields);

const filterMembersNotIncludingUser = (edges: GroupMembersEdge[], userId: User['id']) =>
  edges.filter((edge) => edge.nodeId !== userId);

const memberEdgeCompare = (a: GroupMembersEdge, b: GroupMembersEdge) => a.nodeId === b.nodeId;

const makeMemberGroupAdmin = (
  state: GroupMemberPanels,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupMemberPanels = state.fulfilled.byId[groupId];
  if (!groupMemberPanels) {
    return state;
  }

  const { members } = groupMemberPanels;
  const newGroupAdmin: GroupMembersEdge = {
    isAdmin: true,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupMemberPanels,
    members: arrayUpdateInPlace<GroupMembersEdge>(members, newGroupAdmin, memberEdgeCompare),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const revokeMembersGroupAdminStatus = (
  state: GroupMemberPanels,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupMemberPanels = state.fulfilled.byId[groupId];
  if (!groupMemberPanels) {
    return state;
  }

  const { members } = groupMemberPanels;
  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupMemberPanels,
    members: arrayUpdateInPlace<GroupMembersEdge>(members, newGroupMember, memberEdgeCompare),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const addMemberToGroup = (state: GroupMemberPanels, groupId: Group['id'], userId: User['id']) => {
  const groupMemberPanels = state.fulfilled.byId[groupId];

  if (!groupMemberPanels) {
    return state;
  }

  const { members, pending } = groupMemberPanels;
  if (members.find((edge) => edge.nodeId === userId)) {
    return state;
  }

  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupMemberPanels,
    members: [...members, newGroupMember],
    pending: filterMembersNotIncludingUser(pending, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const removeMemberFromGroup = (
  state: GroupMemberPanels,
  { groupId, userId }: GroupRemoveUserFulfilledAction['payload']
) => {
  const groupMemberPanels = state.fulfilled.byId[groupId];

  if (!groupMemberPanels) {
    return state;
  }

  const { members, pending } = groupMemberPanels;

  const updatedGroupMembers = {
    ...groupMemberPanels,
    members: filterMembersNotIncludingUser(members, userId),
    pending: filterMembersNotIncludingUser(pending, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const handleFulfilledMembershipRequest = (
  state: GroupMemberPanels,
  payload: ResolveGroupMembershipFromMembershipPanelFulfilledAction['payload']
) => {
  const membershipRequestType = payload.requestType;

  return membershipRequestType === 'approve'
    ? addMemberToGroup(state, payload.groupId, payload.userId)
    : removeMemberFromGroup(state, payload);
};

const appendMoreMembers = (state: GroupMemberPanels, action: GroupMemberPanelsLoadMoreFulfilledAction) => {
  const groupId = action.payload.groupId;
  const groupMemberPanels = action.payload.entities.groupMemberPanels[groupId];

  const updatedGroupMembers = {
    members: state.fulfilled.byId[groupId].members.concat(groupMemberPanels.members),
    hasMoreMembers: groupMemberPanels.hasMoreMembers,
  };

  return update(state, groupId, updatedGroupMembers);
};

const reducer: Reducer<GroupMemberPanels, GroupMemberPanelsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_MEMBER_PANELS.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_MEMBER_PANELS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupMemberPanels);
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_FULFILLED':
      return appendMoreMembers(state, action);
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return handleFulfilledMembershipRequest(state, action.payload);
    case 'GROUP.ADD_USER_FULFILLED':
      return addMemberToGroup(state, action.payload.groupId, action.payload.userId);
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return addMemberToGroup(state, action.payload.groupId, action.payload.userId);
    case 'GROUP.REMOVE_USER_FULFILLED':
      return removeMemberFromGroup(state, action.payload);
    case 'GROUP.MAKE_ADMIN_FULFILLED':
    case 'GROUP.ADD_LEADER_FULFILLED':
      return makeMemberGroupAdmin(state, action.payload);
    case 'GROUP.REVOKE_ADMIN_FULFILLED':
      return revokeMembersGroupAdminStatus(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
